import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSpareAxleRequestFormCurrentStep, getSpareAxleRequestFormData } from '~store/wizardSpareAxleRequest/selectors'
import {
  resetForm,
  setCurrentStep,
  SpareAxleRequestFormAvailableSteps,
  updateFormStepValues,
} from '~store/wizardSpareAxleRequest/actions'
import {
  SpareAxleRequestFormSteps,
  SpareAxleRequestPossibleFormStepValues,
} from '~api/models/SpareAxleRequestCustomModels'
import { tupleBuilder } from '~utils/tupleBuilder'
import { useStep1 } from '~app/screens/SpareAxleRequest/components/screens/step1/useStep1'
import { useMultiStepForm } from '~shared/components/reactHookForm/MultiStepForm/useMultiStepForm'
import { Success } from '~app/screens/SpareAxleRequest/components/screens/success/Success'
import MultiStepForm from '~shared/components/reactHookForm/MultiStepForm/MultiStepForm'
import { useStep2 } from '~app/screens/SpareAxleRequest/components/screens/step2/useStep2'
import clsx from 'clsx'
import { useGlobalStyles } from '~shared/styles/globalStyles'
import { useStep3 } from '~app/screens/SpareAxleRequest/components/screens/step3/useStep3'
import { SpareAxleRequestForm } from '@saf/saf-kundenportal-frontend-api/models/SpareAxleRequestForm'
import { isDebugEnv } from '~config/envConfig'
import { DevTool } from '@hookform/devtools'
import { RhfDebugHelper } from '~shared/components/reactHookForm/RhfDebugHelper/RhfDebugHelper'

const SpareAxleRequestFormWizard: React.FC = () => {
  const gs = useGlobalStyles()
  const currentStep = useSelector(getSpareAxleRequestFormCurrentStep)
  const formValues = useSelector(getSpareAxleRequestFormData)
  const dispatch = useDispatch()

  const handleSetStep = useCallback((step: SpareAxleRequestFormAvailableSteps) => {
    dispatch(setCurrentStep(step))
  }, [dispatch])

  const handleSaveStep = useCallback(
    (step: SpareAxleRequestFormSteps, values: SpareAxleRequestPossibleFormStepValues) => {
      dispatch(updateFormStepValues({ step, values }))
    }, [dispatch],
  )

  const handleResetForm = useCallback(() => { dispatch(resetForm()) }, [dispatch])

  const stepOrder = tupleBuilder<SpareAxleRequestFormSteps>()
    .add('step1')
    .add('step2')
    .add('step3')
    .build()

  const step1 = useStep1()
  const step2 = useStep2()
  const step3 = useStep3()

  const {
    ActiveStepComponent,
    multiStepFormProps,
    FormStepper,
    FormButtons,
  } = useMultiStepForm<SpareAxleRequestForm>(
    {
      currentStep,
      handleSetStep,
      stepOrder,
      formValues: formValues,
      handleSaveStep: handleSaveStep,
      handleResetForm: handleResetForm,
      steps: {
        step1: step1,
        step2: step2,
        step3: step3,
      },
      successComponent: Success,
      className: clsx(gs.marginTopTiny, gs.marginBottomTiny),
    },
  )

  return <MultiStepForm {...multiStepFormProps}>
    <FormStepper />
    <ActiveStepComponent />
    <FormButtons />
  </MultiStepForm>
}
export default SpareAxleRequestFormWizard
